/**
 * API service for CardVault backend communication
 */

import axios, { AxiosError, AxiosInstance } from 'axios';
import { CardData, OrderContext } from '../types/CardData';
import { TRANSIENT_FAILURE_MESSAGE } from '../common/constants';

export interface EncryptCardRequest {
  cardNumber: string;
  expiryMonth: string;
  expiryYear: string;
  cvv: string;
  cardholderName: string;
  billingAddress: {
    address1: string;
    address2?: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
  orderContext: {
    orderId: string;
    amount: number;
    currency: string;
    customerId?: string;
    propertyId?: string;
    vendorId: string;
    excludeCards?: string[]; // Card brand names to block, forwarded from OrderContext
  };
}

export interface EncryptCardResponse {
  encryptedPayload: {               // Object: {encryptedData, encryptedKey, iv, tag, timestamp}
    encryptedData: string;
    encryptedKey: string;
    iv: string;
    tag: string;
    timestamp: string;
  };
  keyVersion: string;              // Key version used for encryption (e.g. "v1")
  orderContext: {
    orderId: string;
    amount: number;
    currency: string;
    vendorId: string;
    customerId?: string;
    propertyId?: string;
  };
  last4Digits: string;
  cardType: string;
  correlationId: string;
}

export interface ApiError {
  title: string;
  detail: string;
  status: number;
  traceId?: string;
  correlationId?: string;
  errors?: Record<string, string[]>;
}

/**
 * Type guard to check if an error is already a normalized ApiError
 * (i.e., already transformed by the response interceptor's handleError call).
 */
function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    'title' in error &&
    'detail' in error
  );
}

export class ApiService {
  private client: AxiosInstance;
  private bearerToken: string | null = null;  // Phase 3: Bearer token for authentication
  private correlationId: string | null = null; // Optional fixed correlation ID (overrides auto-generate)
  private kongBaseUrl: string | null = null;    // Kong gateway URL for auth and encryption endpoints

  // maxRetries = 3 means 3 total attempts (1 original + 2 retries)
  // With attempt starting at 1 and condition `attempt < maxRetries`, we retry while attempt is 1 or 2
  private maxRetries: number = 3;
  private retryDelay: number = 1000; // ms

  constructor(baseURL: string) {
    this.client = axios.create({
      baseURL,
      timeout: 30000, // 30 seconds
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor for correlation ID and authentication
    this.client.interceptors.request.use((config) => {
      // Use caller-supplied correlation ID if set, otherwise auto-generate per request
      config.headers['X-Correlation-ID'] = this.correlationId ?? this.generateCorrelationId();

      // Add bearer token if available (Phase 3)
      if (this.bearerToken) {
        config.headers['Authorization'] = `Bearer ${this.bearerToken}`;
      }

      return config;
    });

    // Normalize AxiosError → ApiError in the interceptor so retryRequest always
    // receives a consistent ApiError shape with a top-level status field.
    this.client.interceptors.response.use(
      (response) => response,
      (error) => this.handleError(error)
    );
  }

  /**
   * Sets the bearer token for authentication (Phase 3)
   * @param token - Bearer token from Unified Login
   */
  setBearerToken(token: string): void {
    this.bearerToken = token;
    console.log('[ApiService] Bearer token set for authentication');
  }

  /**
   * Sets a fixed correlation ID to use for all requests.
   * When set, overrides the auto-generated per-request ID.
   * Pass null to revert to auto-generation.
   */
  setCorrelationId(id: string | null): void {
    this.correlationId = id;
  }

  /**
   * Updates the API base URL (Phase 3)
   * @param apiUrl - New API base URL
   */
  setApiUrl(apiUrl: string): void {
    this.client.defaults.baseURL = apiUrl;
    console.log('[ApiService] API URL updated to:', apiUrl);
  }

  /**
   * Sets the Kong gateway URL used as base URL for auth and encryption endpoints.
   * Health endpoints continue to use the regular API base URL.
   * @param url - Kong API gateway URL
   */
  setKongApiUrl(url: string): void {
    this.kongBaseUrl = url;
    console.log('[ApiService] Kong API URL updated');
  }

  /**
   * Encrypts card data via the CardVault API
   * @param cardData - Card data to encrypt
   * @param orderContext - Order context information
   * @returns Encrypted card response
   */
  async encryptCard(
    cardData: CardData,
    orderContext: OrderContext
  ): Promise<EncryptCardResponse> {
    const request: EncryptCardRequest = {
      cardNumber: cardData.cardNumber.replace(/\s/g, ''), // Remove spaces
      expiryMonth: cardData.expiryMonth,
      expiryYear: cardData.expiryYear,
      cvv: cardData.cvv,
      cardholderName: cardData.cardholderName,
      billingAddress: cardData.billingAddress,
      orderContext,
    };

    const config = this.kongBaseUrl ? { baseURL: this.kongBaseUrl } : {};
    return await this.retryRequest(() =>
      this.client.post<EncryptCardResponse>('/v1/card/paymentinstrumentcardencryption', request, config)
    );
  }

  /**
   * Retries a request with exponential backoff
   * @param requestFn - Function that performs the request
   * @returns Response data
   */
  private async retryRequest<T>(
    requestFn: () => Promise<{ data: T }>,
    attempt: number = 1
  ): Promise<T> {
    try {
      const response = await requestFn();
      return response.data;
    } catch (error) {
      // Only retry on network errors or 5xx errors (no 4xx including 429)
      if (this.isRetryableError(error) && attempt < this.maxRetries) {
        const delay = this.retryDelay * Math.pow(2, attempt - 1); // Exponential backoff
        await this.sleep(delay);
        return this.retryRequest(requestFn, attempt + 1);
      }

      // Error is already an ApiError (normalized by response interceptor) — avoid double-normalization.
      // After retries exhausted for any retryable error (network or 5xx), show generic message.
      if (isApiError(error)) {
        if (this.isRetryableError(error) && attempt >= this.maxRetries) {
          error.detail = TRANSIENT_FAILURE_MESSAGE;
        }
        throw error;
      }
      // Fallback: normalize unexpected non-ApiError (e.g., thrown by sleep or internal code)
      return this.handleError(error as AxiosError);
    }
  }

  /**
   * Checks if an error is retryable
   * @param error - Error object
   * @returns true if retryable
   */
  private isRetryableError(error: unknown): boolean {
    // Network errors (ApiError with status 0 or unknown errors without status)
    if (!(error as ApiError)?.status) return true;

    // 5xx server errors only (no 4xx including 429)
    if (((error as ApiError)?.status ?? 500) >= 500) return true;

    return false;
  }

  /**
   * Handles API errors and converts to ApiError format.
   * Called by the response interceptor to normalize AxiosError → ApiError.
   * @param error - Axios error
   * @returns Rejected promise with ApiError
   */
  private handleError(error: AxiosError): Promise<never> {
    const apiError: ApiError = {
      title: 'API Error',
      detail: 'An error occurred while processing your request',
      status: error.response?.status ?? 500,
    };

    if (error.response?.data) {
      const data = error.response.data as {
        title?: string;
        detail?: string;
        traceId?: string;
        correlationId?: string;
        errors?: Record<string, string[]>;
      };
      apiError.title = data.title ?? apiError.title;
      apiError.detail = data.detail ?? apiError.detail;
      apiError.traceId = data.traceId;
      apiError.correlationId = data.correlationId;
      apiError.errors = data.errors;
    } else if (error.request) {
      apiError.title = 'Network Error';
      apiError.detail = 'Unable to connect to the server. Please check your internet connection.';
      apiError.status = 0;
    } else {
      apiError.title = 'Request Error';
      apiError.detail = error.message;
    }

    return Promise.reject(apiError);
  }

  /**
   * Generates a correlation ID for request tracking
   * @returns UUID v4 string
   */
  private generateCorrelationId(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /**
   * Sleep utility for retry delays
   * @param ms - Milliseconds to sleep
   */
  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Validates whether the current bearer token is still accepted by the API.
   *
   * Outcomes:
   * - Returns `true`  — 2xx response; token is valid
   * - Returns `false` — 401 or 403; token is definitively expired or invalid
   * - Throws           — network error or 5xx; auth service is unreachable
   *                      (caller must not treat this as an auth failure)
   *
   * @returns true if the token is valid, false if expired/invalid
   * @throws ApiError when the auth service is unreachable (network or 5xx)
   */
  async validateBearerToken(token: string): Promise<boolean> {
    try {
      this.setBearerToken(token);
      const config = this.kongBaseUrl ? { baseURL: this.kongBaseUrl } : {};
      await this.client.post('/v1/auth/validate-token', null, config);
      return true;
    } catch (error) {
      // In production the response interceptor normalizes AxiosError → ApiError (status at top level).
      // In unit tests the interceptor is mocked as a no-op, so a raw AxiosError-shaped object arrives.
      // Handle both shapes.
      const status = isApiError(error)
        ? error.status
        : (error as AxiosError)?.response?.status;

      // 401/403 → token is definitively invalid; caller should surface an auth error
      if (status === 401 || status === 403) {
        return false;
      }

      // Network failures (status 0 / undefined) and 5xx → auth service unreachable.
      // Do NOT conflate with auth failure — rethrow so the caller can handle separately.
      throw error;
    }
  }

  /**
   * Checks API health
   * @returns Health status
   */
  async checkHealth(): Promise<{ status: string; version: string }> {
    const response = await this.client.get('/health');
    return response.data;
  }
}

/**
 * Creates an API service instance
 * @param baseURL - API base URL (defaults to runtime config, environment variable, or localhost)
 * @returns ApiService instance
 *
 * Configuration priority:
 * 1. baseURL parameter (explicit override)
 * 2. window.CARDVAULT_CONFIG.apiUrl (runtime config from Docker entrypoint)
 * 3. import.meta.env.VITE_API_URL (build-time config for local development)
 * 4. 'http://localhost:5000' (fallback for local development)
 */
export function createApiService(baseURL?: string): ApiService {
  const apiBaseURL =
    baseURL ||
    (window as { CARDVAULT_CONFIG?: { apiUrl: string } }).CARDVAULT_CONFIG?.apiUrl ||  // Runtime config (injected by Docker entrypoint)
    import.meta.env.VITE_API_URL ||               // Build-time config (local dev only)
    'http://localhost:5000';                       // Default fallback

  return new ApiService(apiBaseURL);
}

// Export singleton instance
export const apiService = createApiService();
